import { ActionTabs, Button, v2 } from '@fieldnation/platform-components';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Part,
  PartUsage,
  PartV1,
  USAGE_CODES as USAGE_ENUMS,
} from './types.d';
import { convertPartDataToV1Payload, getWorkorderStatusId, USAGE_CODES } from './utils';
import styles from './styles.scss';
import { error, success } from '../legacy/flash-messenger';
import { createPartApi, updatePartApi } from './service';
import AddPart from './AddPart';
import Usage from './Usage';

const { Modal } = v2;

enum TABS {
  DETAILS = 'Details',
  USAGE = 'Usage',
}

const initialStateNewPart: Part = {
  id: 0,
  externalId: '',
  externalNumber: '',
  description: '',
  returnable: '',
  trackingNumber: '',
  carrier: '',
  carrierOther: '',
  returnInstruction: '',
};

const initialStatePartUsageData: PartUsage = {
  usageCode: '',
  customerRetained: '',
  returnTracking: '',
  returnCarrier: '',
  returnCarrierOtherName: '',
  note: '',
};

interface PartsFormProps {
  partData: Part;
  usageData: PartUsage;
  onStateChange?:
    | ((id: number, type: 'part' | 'usage', data: Part | PartUsage) => void)
    | null;
  onDelete?: ((id: number) => void) | null;
  fromWorkOrderCreate: boolean;
  openEditModal: boolean;
  setOpenEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  partsCorrelationId: string;
  workOrderId: number;
  workOrderProjectName: string;
  workOrderStatus: string;
  workOrderUserRole: string;
  usageCodes?: string[];
  addPart: boolean;
  isProvider: boolean;
}

const PartsForm = ({
  partData,
  usageData,
  onStateChange = null,
  onDelete = null,
  fromWorkOrderCreate = false,
  openEditModal = false,
  setOpenEditModal,
  partsCorrelationId,
  workOrderId = 0,
  workOrderProjectName = '',
  workOrderStatus = '',
  workOrderUserRole = '',
  usageCodes = [],
  addPart = false,
  isProvider = false,
}: PartsFormProps): JSX.Element => {
  const [tab, setTab] = useState(TABS.DETAILS);
  const [newPart, setNewPart] = useState<Part>(partData ?? initialStateNewPart);
  const [partUsageData, setPartUsageData] = useState<PartUsage>(
    usageData ?? initialStatePartUsageData,
  );
  const [returnTrackingRequired, setReturnTrackingRequired] =
    useState<boolean>(false);
  const [isUniqueExternalId, setIsUniqueExternalId] = useState<boolean>(true);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  useEffect(() => {
    // Not a provider and not all required fields are filled
    if (
      !isProvider &&
      (!newPart.externalId?.trim() ||
        !newPart.externalNumber?.trim() ||
        !newPart.description?.trim() ||
        !newPart.returnable?.trim() ||
        !newPart.trackingNumber?.trim())
    ) {
      setCanSubmit(false);
      return;
    }

    // Not a provider and externalId is not unique
    if (!isProvider && !isUniqueExternalId) {
      setCanSubmit(false);
      return;
    }

    // return tracking is required and returnable is yes but return tracking fields are not filled
    if (
      returnTrackingRequired &&
      newPart?.returnable === 'Yes' &&
      (!partUsageData.returnTracking?.trim() ||
        !partUsageData.returnCarrier?.trim() ||
        (partUsageData.returnCarrier === 'other' &&
          !partUsageData.returnCarrierOtherName?.trim()))
    ) {
      setCanSubmit(false);
      return;
    }

    if (
      partUsageData.usageCode === USAGE_ENUMS.USED &&
      !partUsageData.customerRetained &&
      newPart?.returnable === 'Yes'
    ) {
      setCanSubmit(false);
      return;
    }

    if (!newPart.returnInstruction) {
      setCanSubmit(false);
      return;
    }

    if (isProvider && !partUsageData?.usageCode) {
      setCanSubmit(false);
      return;
    }

    setCanSubmit(true);
  }, [newPart, partUsageData, returnTrackingRequired, isUniqueExternalId]);

  const onClose = () => {
    if (!fromWorkOrderCreate) {
      setOpenEditModal(false);
    }
  };

  const handleCreateAndUpdate = async () => {
    const companyId = window?.context?.company?.id || 0;
    const statusId = getWorkorderStatusId(workOrderStatus);

    const payload = convertPartDataToV1Payload(newPart, partUsageData);
    let partResponse: PartV1 | undefined;

    if (addPart) {
      partResponse = await createPartApi(
        workOrderId,
        workOrderUserRole,
        companyId,
        statusId,
        payload,
      );
    } else {
      partResponse = await updatePartApi(
        workOrderId,
        newPart.id,
        workOrderUserRole,
        companyId,
        statusId,
        payload,
      );
    }

    if (!partResponse) {
      error('Failed to save part information, please try again later.');
      return;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-unused-expressions
    window?._fnRedux?.dispatch({
      type: 'UPDATE_WORK_ORDER_PARTS',
      correlation_id: partsCorrelationId,
      payload: partResponse,
    });
    success('Part information updated.');
  };

  const onSave = async () => {
    if (!fromWorkOrderCreate) {
      if (!addPart) {
        // eslint-disable-next-line no-unused-expressions
        window?._amplitudeClient?.logEvent('Completed Parts Usage', {
          'Work Order ID': workOrderId,
          'Project Name': workOrderProjectName ?? '',
          'Usage Code': partUsageData?.usageCode,
          'Work Order Status': workOrderStatus ?? '',
          'Part ID': newPart?.externalId,
          'OEM Number ': newPart?.externalNumber,
          'Part Description': newPart?.description,
          'Retained by Customer': partUsageData?.customerRetained,
          Returnable: newPart?.returnable,
          'Return Tracking Number': partUsageData?.returnTracking,
          Carrier: newPart?.carrier,
          Note: partUsageData?.note,
        });
      }
      try {
        await handleCreateAndUpdate();
        // eslint-disable-next-line no-empty
      } catch (_) {
        error(
          `Failed to ${
            addPart ? 'save' : 'update'
          } part information, please try again later.`,
        );
      }
      onClose();
    }
  };

  const renderProvider = () => (
    <Usage
      partData={newPart}
      usageData={partUsageData}
      setUsageData={setPartUsageData}
      usageCodes={usageCodes}
      onStateChange={onStateChange}
      fromWorkOrderCreate={fromWorkOrderCreate}
      returnTrackingRequired={returnTrackingRequired}
      setReturnTrackingRequired={setReturnTrackingRequired}
      isProvider={isProvider}
    />
  );

  const partsForm = isProvider ? (
    <>{renderProvider()}</>
  ) : (
    <>
      {fromWorkOrderCreate ? (
        <div className={styles['delete-button']}>
          <Button
            icon="deleteHollow"
            onClick={() => {
              if (onDelete && newPart?.id) {
                onDelete(newPart.id);
              }
            }}
            shouldUseIconComponent
            type="text"
          />
        </div>
      ) : null}

      {!addPart && !fromWorkOrderCreate && (
        <ActionTabs
          items={[
            {
              onClick: () => setTab(TABS.DETAILS),
              label: 'Details',
              active: tab === TABS.DETAILS,
            },
            {
              onClick: () => setTab(TABS.USAGE),
              label: 'Usage',
              active: tab === TABS.USAGE,
            },
          ]}
        />
      )}

      {tab === TABS.DETAILS && (
        <AddPart
          newPart={newPart}
          setNewPart={setNewPart}
          usageData={partUsageData}
          setUsageData={setPartUsageData}
          onStateChange={onStateChange}
          fromWorkOrderCreate={fromWorkOrderCreate}
          addPart={addPart}
          isUniqueExternalId={isUniqueExternalId}
          setIsUniqueExternalId={setIsUniqueExternalId}
        />
      )}

      {tab === TABS.USAGE && (
        <Usage
          partData={newPart}
          usageData={partUsageData}
          setUsageData={setPartUsageData}
          usageCodes={usageCodes}
          onStateChange={onStateChange}
          fromWorkOrderCreate={fromWorkOrderCreate}
          returnTrackingRequired={returnTrackingRequired}
          setReturnTrackingRequired={setReturnTrackingRequired}
          isProvider={isProvider}
        />
      )}
    </>
  );

  const getModalHeader = useCallback(
    () => (isProvider ? 'Enter part usage' : `${addPart ? 'Add' : 'Edit'} part`),
    [addPart, isProvider],
  );

  return (
    <>
      {!fromWorkOrderCreate ? (
        <Modal
          header={getModalHeader()}
          isOpen={openEditModal}
          onClose={onClose}
          size="medium"
          position="default"
          footerActions={[
            {
              label: 'Cancel',
              type: 'secondary',
              onClick: onClose,
            },
            {
              label: 'Save',
              type: 'primary',
              onClick: onSave,
              disabled: !canSubmit,
            },
          ]}
        >
          {partsForm}
        </Modal>
      ) : (
        <>{partsForm}</>
      )}
    </>
  );
};

PartsForm.defaultProps = {
  usageCodes: USAGE_CODES,
  onStateChange: null,
  onDelete: null,
};

export default PartsForm;
